import React, { useState, useEffect } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  AutocompleteInput,
  BooleanInput,
  ReferenceInput,
  required,
  SaveButton,
  Toolbar,
  useRecordContext,
} from 'react-admin';
import { Card, Typography, Box, Grid, Divider } from '@mui/material';
import {
  NotificationImportant as AlertIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionTransform } from '../../hooks/useRolePermissionTransform';

const AlertCreateToolbar = () => (
  <Toolbar>
    <SaveButton
      variant='contained'
      label='Crear Alerta'
      icon={<SaveIcon />}
      sx={{
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </Toolbar>
);

const statusChoices = [
  { id: 'STANDBY', name: 'En espera' },
  { id: 'ACKNOWLEDGED', name: 'Reconocida' },
  { id: 'INACTIVE', name: 'Inactiva' },
  { id: 'ACTIVE', name: 'Activa' },
];

export const AlertsCreate = () => {
  const [selectedVariable, setSelectedVariable] = useState<number | null>(null);
  const [selectedZone, setSelectedZone] = useState<number | null>(null);
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  
  const { transformRoles } = useRolePermissionTransform();

  const handleVariableFilterChange = (
    selections: HierarchicalFilterSelections
  ) => {
    // Actualizar el ID de tabla seleccionado
    setSelectedTable(selections.tableId || null);
    // Limpiar la variable seleccionada cuando cambia la tabla
    if (selections.tableId !== selectedTable) {
      setSelectedVariable(null);
    }
  };

  const handleZoneFilterChange = (selections: HierarchicalFilterSelections) => {
    setSelectedZone(selections.zoneId || null);
  };

  return (
    <Create redirect='show' transform={transformRoles}>
      <Box sx={{ padding: 3 }}>
        <Box display='flex' alignItems='center' mb={3}>
          <AlertIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant='h4' component='h1' fontWeight='bold'>
              Crear Nueva Alerta
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Configura una nueva alerta para el sistema de monitoreo
            </Typography>
          </Box>
        </Box>

        <Card sx={{ boxShadow: 3, p: 3 }}>
          <SimpleForm toolbar={<AlertCreateToolbar />}>
            <Grid container spacing={3}>
              {/* Información básica */}
              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Información Básica
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source='name'
                  label='Nombre de la Alerta'
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput source='category' label='Categoría' fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source='description'
                  label='Descripción'
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source='text'
                  label='Texto de la Alerta'
                  multiline
                  rows={2}
                  fullWidth
                />
              </Grid>
              {/* Configuración de límites */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Límites de Activación
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumberInput
                  source='veryHighLimit'
                  label='Límite Muy Alto'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumberInput source='highLimit' label='Límite Alto' fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumberInput source='lowLimit' label='Límite Bajo' fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumberInput
                  source='veryLowLimit'
                  label='Límite Muy Bajo'
                  fullWidth
                />
              </Grid>
              {/* Referencias */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Selección de Variable
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Selecciona la planta, zona y tabla para filtrar las
                    variables disponibles:
                  </Typography>
                  <HierarchicalFilter
                    levels={['plant', 'zone', 'table']}
                    onChange={handleVariableFilterChange}
                    size='small'
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <ReferenceInput
                  source='variableId'
                  reference='variables'
                  sort={{ field: 'variableName', order: 'ASC' }}
                  filter={selectedTable ? { tableId: selectedTable } : {}}
                >
                  <AutocompleteInput
                    optionText='variableName'
                    optionValue='id'
                    label='Variable'
                    fullWidth
                    disabled={!selectedTable}
                    helperText={
                      selectedTable
                        ? 'Busca y selecciona la variable específica para la alerta'
                        : 'Primero selecciona una tabla en el filtro de arriba'
                    }
                    filterToQuery={searchText => ({ q: searchText })}
                    noOptionsText='No se encontraron variables'
                  />
                </ReferenceInput>
              </Grid>
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Zona de la Alerta
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Selecciona la planta y zona donde se guardará esta alerta:
                  </Typography>
                  <HierarchicalFilter
                    levels={['plant', 'zone']}
                    onChange={handleZoneFilterChange}
                    size='small'
                  />
                </Box>
                {/* Campo oculto para el zoneId */}
                {selectedZone && (
                  <input type='hidden' name='zoneId' value={selectedZone} />
                )}
              </Grid>
              {/* Roles de acceso */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Roles de Acceso
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12}>
                <RolePermissionManager
                  source="roles"
                  title="Gestión de Roles para Alerta"
                  subtitle="Configura los roles que tendrán acceso a esta alerta y su nivel de permisos"
                  helperText="Selecciona roles y asigna niveles de permisos (READ, WRITE, ADMIN) para controlar el acceso a esta alerta"
                />
              </Grid>
            </Grid>
          </SimpleForm>
        </Card>
      </Box>
    </Create>
  );
};
